import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Tab, Tabs, TextField, Tooltip, Typography } from "@mui/material";
import { ApiOutlined, CheckCircleOutline, Close, ContentCopyOutlined, KeyOutlined, KeyboardArrowDownRounded, ManageSearchOutlined, RefreshOutlined, SearchOutlined, Send, VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import PropTypes from "prop-types";
import { createOperationClient } from "@preezie/widget-sdk";
import axiosInstance from "../axiosInstance";
import axiosInstanceWidget, { BASE_API_URL as WIDGET_API_URL } from "../axiosInstanceWidget";
import TenantAutocomplete from "./TenantAutocomplete";
import ProductPickerDialog from "./ProductPickerDialog";
import OperationResponsePanel from "./OperationResponsePanel";
import LogAgents from "./LogAgents";
import TraceIdLink from "./TraceIdLink";

const API_TABS = ["External triggers", "Product exists", "API key"];

const INTENT_OPTIONS = [
  "ProductSearch",
  "ProductCompare",
  "ProductBundling",
  "SimilarProductEnquiry",
];

const INTENT_ENDPOINTS = {
  ProductSearch: "/api/Operation/search",
  ProductCompare: "/api/Operation/compare",
  ProductBundling: "/api/Operation/bundle",
  SimilarProductEnquiry: "/api/Operation/similar-to",
};

const INTENT_SDK_METHODS = {
  ProductSearch: "search",
  ProductCompare: "compare",
  ProductBundling: "bundle",
  SimilarProductEnquiry: "similarTo",
};

const WIDGET_SDK_BASE_URL = WIDGET_API_URL.replace(/\/api\/?$/i, "");

const PRODUCT_ID_FIELD_COUNT = {
  ProductBundling: 1,
  SimilarProductEnquiry: 1,
  ProductCompare: 2,
};

const DEFAULT_BODY_PLACEHOLDER = '{\n  "key": "value"\n}';

const getCredentialValue = (data) => {
  if (typeof data === "string") return data;
  if (!data || typeof data !== "object") return "";
  const candidates = [
    data.apiKey,
    data.api_key,
    data.publicEndpointCredential,
    data.publicEndpointApiKey,
    data.key,
    data.credential,
    data.value,
    data.token,
    data.data,
  ];
  const value = candidates.find((candidate) => typeof candidate === "string");
  if (value) return value;
  if (data.data && typeof data.data === "object") return getCredentialValue(data.data);
  return JSON.stringify(data, null, 2);
};

const getRequestError = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return data?.message || data?.errorMessage || error?.message || fallback;
};

const getTraceIdFromHeaders = (headers) => {
  if (!headers) return "";
  if (typeof headers.get === "function") {
    const value = headers.get("trace-id");
    return typeof value === "string" ? value.trim() : "";
  }
  const entries = typeof headers === "object" ? Object.entries(headers) : [];
  const match = entries.find(([key]) => key.toLowerCase() === "trace-id");
  return typeof match?.[1] === "string" ? match[1].trim() : "";
};

const getTraceId = (headers) => getTraceIdFromHeaders(headers);

const toBase64Url = (bytes) => {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const encodeJwtSegment = (value) =>
  toBase64Url(new TextEncoder().encode(JSON.stringify(value)));

const decodeBase64 = (value) => {
  const binary = window.atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

const mintOperationToken = async ({ tenantId, signingKey }) => {
  if (!window.crypto?.subtle) {
    throw new Error("This browser does not support the Web Crypto API required to mint the operation token.");
  }

  const issuedAt = Math.floor(Date.now() / 1000);
  const header = { alg: "HS256", typ: "JWT", tenantId };
  const payload = { tenantId, iat: issuedAt, exp: issuedAt + 300 };
  const signingInput = `${encodeJwtSegment(header)}.${encodeJwtSegment(payload)}`;
  const key = await window.crypto.subtle.importKey(
    "raw",
    decodeBase64(signingKey),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  const signature = await window.crypto.subtle.sign(
    "HMAC",
    key,
    new TextEncoder().encode(signingInput)
  );
  return `${signingInput}.${toBase64Url(new Uint8Array(signature))}`;
};

const generateUuid = () => {
  const c = typeof window !== "undefined" ? window.crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const INTENT_BODY_TEMPLATE = {
  ProductCompare: () =>
    JSON.stringify(
      {
        content: "Compare selected products",
        chatMetaData: {
          websiteUrl:
            "https://dev-greenback-ui-widget.pages.dev/?tid=tnt_pJ22NGJQXirBTBV&preezie-shop-assistant-preview=true&app_type=pdp",
        },
        productIds: ["<product-id-1>", "<product-id-2>"],
        sessionId: generateUuid(),
        visitorId: generateUuid(),
        requestOrigin: "Chat",
        lastContent: "",
        pageSize: 12,
        OperationHideAiMessage: false,
        OperationReturnFullProductInfo: true,
      },
      null,
      2
    ),
  SimilarProductEnquiry: () =>
    JSON.stringify(
      {
        content: "Show similar products to this",
        chatMetaData: {
          productId: "<product-id>",
          title: "<product-title>",
          websiteUrl: "https://your-store.example.com",
        },
        sessionId: generateUuid(),
        visitorId: generateUuid(),
        requestOrigin: "Chat",
        lastContent: "",
        pageSize: 12,
        OperationHideAiMessage: false,
        OperationReturnFullProductInfo: true,
      },
      null,
      2
    ),
};

const INTENT_DEFAULT_CONTENT = {
  ProductBundling: "Find products to bundle with this product",
};

const buildDefaultBody = (intent) => {
  const count = PRODUCT_ID_FIELD_COUNT[intent] || 0;
  const chatMetaData = { websiteUrl: "https://your-store.example.com" };
  if (count === 1) {
    chatMetaData.productId = "<product-id>";
    chatMetaData.title = "<product-title>";
  }
  const body = {
    content: INTENT_DEFAULT_CONTENT[intent] || "",
    chatMetaData,
    ...(count >= 2 ? { productIds: Array.from({ length: count }, (_, i) => `<product-id-${i + 1}>`) } : {}),
    sessionId: generateUuid(),
    visitorId: generateUuid(),
    requestOrigin: "Chat",
    lastContent: "",
    pageSize: 12,
    OperationHideAiMessage: false,
    OperationReturnFullProductInfo: true,
  };
  return JSON.stringify(body, null, 2);
};

const deriveProductUrl = (product) => {
  const link = typeof product?.link === "string" ? product.link.trim() : "";
  if (link) {
    try {
      return new URL(link).pathname;
    } catch {
      return link.startsWith("/") ? link : `/${link}`;
    }
  }
  return product?.id ? `/products/${product.id}` : "";
};

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    minHeight: 56,
    borderRadius: "16px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
    "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
    "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
  },
  "& label.Mui-focused": { color: "#f97316" },
};

const mapProduct = (product) => ({
  id: product?.Id ?? product?.id,
  title: product?.Title ?? product?.title ?? "Untitled product",
  link: product?.Link ?? product?.link ?? product?.Variants?.[0]?.VariantUrl ?? "",
});

const applyProductIdToBody = (body, productId, field, previousProduct) => {
  if (!body || !body.trim()) return body;
  let parsed;
  try {
    parsed = JSON.parse(body);
  } catch {
    return body;
  }

  if (Array.isArray(parsed.productIds)) {
    const index = field === "productId2" ? 1 : 0;
    parsed.productIds[index] = productId;
  }

  if (field !== "productId2") {
    const meta = parsed.chatMetaData;
    if (meta && typeof meta === "object" && !Array.isArray(meta)) {
      if ("productId" in meta) meta.productId = productId;
      if (previousProduct?.title && meta.title === previousProduct.title) {
        meta.title = "<product-title>";
      }
      if (previousProduct?.link && meta.websiteUrl === previousProduct.link) {
        meta.websiteUrl = "https://your-store.example.com";
      }
    }
  }

  return JSON.stringify(parsed, null, 2);
};

const applyProductToBody = (body, product, field) => {
  if (!body || !body.trim()) return body;
  let parsed;
  try {
    parsed = JSON.parse(body);
  } catch {
    return body;
  }
  if (Array.isArray(parsed.productIds)) {
    const index = field === "productId2" ? 1 : 0;
    parsed.productIds[index] = product.id;
  }
  const meta = parsed.chatMetaData;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    if ("productId" in meta) meta.productId = product.id;
    if ("title" in meta) meta.title = product.title;
    if ("websiteUrl" in meta && product.link) meta.websiteUrl = product.link;
  }
  let out = JSON.stringify(parsed, null, 2);
  if (product.title) out = out.split("<product-title>").join(product.title);
  return out;
};

const applyProductsToBody = (body, products, count) => {
  let out = body;
  for (let i = 0; i < count; i += 1) {
    if (products[i]) {
      out = applyProductToBody(out, products[i], i === 1 ? "productId2" : "productId");
    }
  }
  return out;
};

const applyContentToBody = (body, content) => {
  if (!body || !body.trim()) return body;
  let parsed;
  try {
    parsed = JSON.parse(body);
  } catch {
    return body;
  }
  parsed.content = content;
  return JSON.stringify(parsed, null, 2);
};

const parseOperationStream = (raw) => {
  if (typeof raw !== "string") return { message: "", products: [] };
  let message = "";
  let products = [];
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data:")) continue;
    const payload = trimmed.slice(5).trim();
    if (!payload) continue;
    let parsed;
    try {
      parsed = JSON.parse(payload);
    } catch {
      continue;
    }
    if (typeof parsed.chatStream === "string") {
      message += parsed.chatStream;
    } else if (parsed.data && Array.isArray(parsed.data.responsePayload)) {
      products = parsed.data.responsePayload.filter((p) => p !== null);
    }
  }
  return { message, products };
};

const consumeOperationResponse = async (response, onProgress) => {
  if (!response.body || typeof response.body.getReader !== "function") {
    const text = await response.text();
    onProgress(text);
    return text;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let raw = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    raw += decoder.decode(value, { stream: true });
    onProgress(raw);
  }
  raw += decoder.decode();
  onProgress(raw);
  return raw;
};

const ApisTab = ({ tenants: externalTenants = [], selectedTenant, setSelectedTenant }) => {
  const [tenantOptions, setTenantOptions] = useState(externalTenants);
  const [loadingTenants, setLoadingTenants] = useState(false);

  useEffect(() => {
    if (externalTenants.length) setTenantOptions(externalTenants);
  }, [externalTenants]);

  useEffect(() => {
    if (externalTenants.length) return;

    (async () => {
      setLoadingTenants(true);
      try {
        const { data } = await axiosInstance.get("/tenant/alltenants");
        setTenantOptions(data ?? []);
      } catch {
      } finally {
        setLoadingTenants(false);
      }
    })();
  }, [externalTenants]);

  const [tenantId, setTenantId] = useState(selectedTenant ?? "");
  useEffect(() => {
    if (selectedTenant !== undefined) setTenantId(selectedTenant);
  }, [selectedTenant]);
  const notifyParent = typeof setSelectedTenant === "function" ? setSelectedTenant : () => {};
  const currentTenant = tenantOptions.find((t) => t.id === tenantId) || null;

  const [tabValue, setTabValue] = useState(0);
  const [intentName, setIntentName] = useState("");
  const [productId, setProductId] = useState("");
  const [productId2, setProductId2] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [jsonBody, setJsonBody] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [sending, setSending] = useState(false);
  const [responseInfo, setResponseInfo] = useState(null);
  const productIdCount = PRODUCT_ID_FIELD_COUNT[intentName] || 0;
  const isProductSearch = intentName === "ProductSearch";

  const [existingProductId, setExistingProductId] = useState("");
  const [existingUrl, setExistingUrl] = useState("");
  const [existingChecking, setExistingChecking] = useState(false);
  const [existingResult, setExistingResult] = useState(null);
  const [selectedLogTraceId, setSelectedLogTraceId] = useState("");
  const [logModalOpen, setLogModalOpen] = useState(false);

  const [credential, setCredential] = useState("");
  const [credentialLoading, setCredentialLoading] = useState("");
  const [credentialError, setCredentialError] = useState("");
  const [credentialVisible, setCredentialVisible] = useState(false);
  const [credentialCopied, setCredentialCopied] = useState(false);
  const credentialRequest = useRef("");

  useEffect(() => {
    credentialRequest.current = generateUuid();
    setCredential("");
    setCredentialError("");
    setCredentialVisible(false);
    setCredentialCopied(false);
  }, [tenantId]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerField, setPickerField] = useState("productId");
  const [productLookups, setProductLookups] = useState({});
  const productLookupRequests = useRef({});

  const openPicker = (field) => {
    setPickerField(field);
    setPickerOpen(true);
  };

  const selectProduct = (product, field, closePicker = false) => {
    if (field === "existingProductId") {
      setExistingProductId(product.id);
      setExistingUrl(deriveProductUrl(product));
      setExistingResult(null);
      if (closePicker) setPickerOpen(false);
      return;
    }
    const slot = field === "productId2" ? 1 : 0;
    setSelectedProducts((prev) => {
      const nextProducts = [...prev];
      nextProducts[slot] = product;
      return nextProducts;
    });
    if (field === "productId2") setProductId2(product.id);
    else setProductId(product.id);
    setJsonBody((prev) => applyProductToBody(prev, product, field));
    setProductLookups((prev) => ({
      ...prev,
      [field]: { loading: false, product, error: "" },
    }));
    if (closePicker) setPickerOpen(false);
  };

  const handleSelectProduct = (product) => {
    selectProduct(product, pickerField, true);
  };

  const handleProductIdChange = (field, value) => {
    const slot = field === "productId2" ? 1 : 0;
    const previousProduct = selectedProducts[slot];
    productLookupRequests.current[field] = generateUuid();
    if (field === "productId2") setProductId2(value);
    else setProductId(value);
    setJsonBody((prev) => applyProductIdToBody(prev, value.trim(), field, previousProduct));
    setSelectedProducts((prev) => {
      if (!prev[slot] || String(prev[slot].id) === value.trim()) return prev;
      const nextProducts = [...prev];
      nextProducts[slot] = undefined;
      return nextProducts;
    });
    setProductLookups((prev) => ({
      ...prev,
      [field]: { loading: false, product: null, error: "" },
    }));
  };

  const findProductById = async (field, rawProductId) => {
    const value = rawProductId.trim();
    if (!tenantId || !value) return;
    const requestId = generateUuid();
    productLookupRequests.current[field] = requestId;

    setProductLookups((prev) => ({
      ...prev,
      [field]: { loading: true, product: null, error: "" },
    }));

    try {
      const { data } = await axiosInstance.post(
        `/product/${tenantId}/search`,
        { query: value, productsPerPage: 10, page: 1, tenantId },
        { headers: { TenantId: tenantId } }
      );
      const rawProducts = data?.products ?? data?.items ?? [];
      const product = rawProducts
        .map(mapProduct)
        .find((candidate) => String(candidate.id).toLowerCase() === value.toLowerCase());

      if (productLookupRequests.current[field] !== requestId) return;
      if (!product) {
        setProductLookups((prev) => ({
          ...prev,
          [field]: { loading: false, product: null, error: "Product not found" },
        }));
        return;
      }

      selectProduct(product, field);
    } catch {
      if (productLookupRequests.current[field] !== requestId) return;
      setProductLookups((prev) => ({
        ...prev,
        [field]: { loading: false, product: null, error: "Could not load product" },
      }));
    }
  };

  const handleProductIdPaste = (event, field) => {
    const pastedValue = event.clipboardData.getData("text").trim();
    if (!pastedValue) return;
    event.preventDefault();
    handleProductIdChange(field, pastedValue);
    findProductById(field, pastedValue);
  };

  const handleSendRequest = async () => {
    setJsonError("");
    setResponseInfo(null);
    let parsedBody = {};
    if (jsonBody.trim()) {
      try {
        parsedBody = JSON.parse(jsonBody);
      } catch {
        setJsonError("Invalid JSON. Please provide a valid JSON object.");
        return;
      }
    }
    setSending(true);
    try {
      const sdkMethod = INTENT_SDK_METHODS[intentName];
      if (!operationClient || !sdkMethod) throw new Error("Select a supported intent before sending the request.");
      const response = await operationClient[sdkMethod](parsedBody);
      const responseTraceId = getTraceId(response.headers);
      const rawText = await consumeOperationResponse(response, (text) => {
        if (!text) return;
        const partial = parseOperationStream(text);
        setResponseInfo({
          ok: true,
          status: response.status,
          message: partial.message,
          products: partial.products,
          raw: text,
          traceId: responseTraceId,
        });
      });
      const parsed = parseOperationStream(rawText);
      setResponseInfo({
        ok: true,
        status: response.status,
        message: parsed.message,
        products: parsed.products,
        raw: rawText,
        traceId: responseTraceId,
      });
    } catch (error) {
      const errData = error?.response?.data;
      const rawErr =
        typeof errData === "string" ? errData : errData ? JSON.stringify(errData, null, 2) : error?.message;
      const parsed = parseOperationStream(rawErr);
      setResponseInfo({
        ok: false,
        status: error?.response?.status,
        message: parsed.message,
        products: parsed.products,
        raw: rawErr,
        traceId: getTraceId(error?.response?.headers),
      });
    } finally {
      setSending(false);
    }
  };

  const buildExistingProductUrl = (raw) => {
    const value = raw.trim();
    if (!value) return "";
    return value.startsWith("/") ? value : `/products/${value}`;
  };

  const handleCheckExistingProduct = async () => {
    const url = existingUrl.trim();
    if (!url) return;
    setExistingChecking(true);
    setExistingResult(null);
    try {
      const { data, status, headers } = await axiosInstanceWidget.post(
        "/products/exists",
        { url },
        { headers: { TenantId: tenantId } }
      );
      const rawText = typeof data === "string" ? data : JSON.stringify(data, null, 2);
      setExistingResult({ ok: true, status, raw: rawText, traceId: getTraceId(headers) });
    } catch (error) {
      const errData = error.response?.data;
      const rawErr =
        typeof errData === "string" ? errData : errData ? JSON.stringify(errData, null, 2) : error.message;
      setExistingResult({
        ok: false,
        status: error.response?.status,
        raw: rawErr,
        traceId: getTraceId(error.response?.headers),
      });
    } finally {
      setExistingChecking(false);
    }
  };

  const credentialEndpoint = `/tenant/${encodeURIComponent(tenantId)}/public-endpoint-credential`;

  const operationClient = useMemo(() => {
    if (!tenantId) return null;
    return createOperationClient({
      tenantId,
      baseUrl: WIDGET_SDK_BASE_URL,
      getToken: async () => {
        const { data } = await axiosInstance.get(credentialEndpoint);
        const signingKey = getCredentialValue(data);
        if (!signingKey) throw new Error("No public endpoint credential is available for this tenant.");
        return mintOperationToken({ tenantId, signingKey });
      },
    });
  }, [credentialEndpoint, tenantId]);

  const handleCredentialRequest = useCallback(async (method) => {
    const requestId = generateUuid();
    credentialRequest.current = requestId;
    setCredentialLoading(method);
    setCredentialError("");
    setCredentialCopied(false);
    try {
      const { data } = await axiosInstance[method](credentialEndpoint);
      if (credentialRequest.current !== requestId) return;
      const nextCredential = getCredentialValue(data);
      if (!nextCredential) {
        setCredential("");
        setCredentialError("The request succeeded, but no API key was returned.");
        return;
      }
      setCredential(nextCredential);
      setCredentialVisible(false);
    } catch (error) {
      if (credentialRequest.current !== requestId) return;
      const fallbackByMethod = {
        get: "Could not retrieve the API key.",
        post: "Could not provision the API key.",
        put: "Could not rotate the API key.",
      };
      setCredentialError(
        getRequestError(error, fallbackByMethod[method])
      );
    } finally {
      if (credentialRequest.current === requestId) setCredentialLoading("");
    }
  }, [credentialEndpoint]);

  useEffect(() => {
    if (tabValue === 2 && tenantId) handleCredentialRequest("get");
  }, [handleCredentialRequest, tabValue, tenantId]);

  const handleCopyCredential = async () => {
    try {
      await navigator.clipboard.writeText(credential);
      setCredentialCopied(true);
    } catch {
      setCredentialCopied(false);
      setCredentialError("Could not copy the API key to the clipboard.");
    }
  };

  const handleOpenTrace = (traceId) => {
    if (!traceId) return;
    setSelectedLogTraceId(traceId);
    setLogModalOpen(true);
  };

  return (
    <Box
      sx={{
        maxWidth: 1400,
        mx: "auto",
        px: { xs: 1, md: 2 },
        py: { xs: 1, md: 2 },
      }}
    >
      <Box
        sx={{
          borderRadius: { xs: 4, md: 6 },
          background: "radial-gradient(circle at top center, rgba(255, 191, 154, 0.28), rgba(255,255,255,0.94) 42%), linear-gradient(180deg, rgba(255, 248, 243, 0.96), rgba(255, 255, 255, 0.92))",
          boxShadow: "0 24px 60px rgba(236, 123, 55, 0.10)",
          border: "1px solid rgba(255, 132, 76, 0.10)",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2.25,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              width: { xs: 56, md: 64 },
              height: { xs: 56, md: 64 },
              borderRadius: "20px",
              background: "linear-gradient(180deg, #ffb58d 0%, #ff8b47 100%)",
              boxShadow: "0 18px 38px rgba(255, 139, 71, 0.30)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <ApiOutlined sx={{ fontSize: 30 }} />
          </Box>
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: "2rem", md: "2.55rem" },
                lineHeight: 1.05,
                fontWeight: 800,
                color: "#202531",
                letterSpacing: "-0.03em",
                mb: 0.8,
              }}
            >
              APIs
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6b7280",
                fontWeight: 500,
              }}
            >
              Manage API requests and integrations across tenants.
            </Typography>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{
            mb: 3.25,
            borderRadius: "24px",
            border: "1px solid rgba(255, 132, 76, 0.10)",
            backgroundColor: "rgba(255,255,255,0.88)",
            boxShadow: "0 20px 44px rgba(236, 123, 55, 0.08)",
            overflow: "visible",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: { xs: 2, md: 3 } } }}>
            <Box sx={{ maxWidth: 520 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#6b7280",
                  fontWeight: 700,
                  mb: 0.9,
                  px: 0.5,
                }}
              >
                Select Tenant
              </Typography>
              <TenantAutocomplete
                tenants={tenantOptions}
                loading={loadingTenants}
                value={currentTenant}
                onChange={(_, v) => {
                  const id = v?.id ?? "";
                  setTenantId(id);
                  notifyParent(id);
                }}
                placeholder="Choose a tenant"
                popupIcon={<KeyboardArrowDownRounded sx={{ color: "#6b7280" }} />}
              />
            </Box>
          </CardContent>
        </Card>

        {!tenantId ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            mt={4}
            mb={4}
          >
            <ApiOutlined sx={{ fontSize: 60, color: "#cbd5e1", mb: 1 }} />
            <Typography variant="body1" sx={{ color: "#4b5563", fontWeight: 700 }}>
              Select a tenant to get started
            </Typography>
            <Typography variant="caption" sx={{ color: "#9ca3af", mt: 0.5 }}>
              Choose a tenant above to manage its APIs
            </Typography>
          </Box>
        ) : (
          <Box>
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              aria-label="API category tabs"
              sx={{
                mb: 2.5,
                minHeight: 0,
                "& .MuiTabs-indicator": {
                  height: 3,
                  borderRadius: "999px",
                  background: "linear-gradient(90deg, #ff8a3d, #ff6d1b)",
                },
                "& .MuiTabs-flexContainer": { gap: 0.5 },
                "& .MuiTab-root": {
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  py: 1.2,
                  px: 1.6,
                  minHeight: 44,
                  borderRadius: "14px",
                  color: "#4b5563",
                  "&.Mui-selected": { color: "#f97316" },
                  "&:hover": { color: "#f97316", backgroundColor: "rgba(255, 247, 241, 0.72)" },
                },
              }}
            >
              {API_TABS.map((label) => (
                <Tab key={label} label={label} />
              ))}
            </Tabs>

            {tabValue === 0 ? (
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: `1fr ${isProductSearch ? "1fr " : ""}${"1fr ".repeat(productIdCount)}auto` },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  <TextField
                    select
                    label="Intent name"
                    value={intentName}
                    onChange={(e) => {
                      const next = e.target.value;
                      setIntentName(next);
                      setSearchText("");
                      const count = PRODUCT_ID_FIELD_COUNT[next] || 0;
                      setProductId(count >= 1 && selectedProducts[0] ? selectedProducts[0].id : "");
                      setProductId2(count >= 2 && selectedProducts[1] ? selectedProducts[1].id : "");
                      const buildTemplate = INTENT_BODY_TEMPLATE[next];
                      const template = next
                        ? buildTemplate
                          ? buildTemplate()
                          : buildDefaultBody(next)
                        : "";
                      setJsonBody(applyProductsToBody(template, selectedProducts, count));
                      setJsonError("");
                    }}
                    fullWidth
                    sx={FIELD_SX}
                  >
                    {INTENT_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </TextField>

                  {isProductSearch && (
                    <TextField
                      label="Search"
                      placeholder="Type what you're looking for"
                      value={searchText}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSearchText(v);
                        setJsonBody((prev) => applyContentToBody(prev, v));
                      }}
                      fullWidth
                      sx={FIELD_SX}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchOutlined fontSize="small" sx={{ color: "#f97316" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  {productIdCount >= 1 && (
                    <TextField
                      label={productIdCount === 2 ? "Product ID 1" : "Product ID"}
                      placeholder="Enter product ID"
                      value={productId}
                      onChange={(e) => handleProductIdChange("productId", e.target.value)}
                      onPaste={(e) => handleProductIdPaste(e, "productId")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") findProductById("productId", productId);
                      }}
                      fullWidth
                      sx={FIELD_SX}
                      error={Boolean(productLookups.productId?.error)}
                      helperText={
                        productLookups.productId?.error ||
                        productLookups.productId?.product?.title ||
                        (productLookups.productId?.loading ? "Searching for product…" : " ")
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {productLookups.productId?.loading ? (
                              <CircularProgress size={18} sx={{ color: "#f97316", mr: 0.5 }} />
                            ) : productLookups.productId?.product ? (
                              <CheckCircleOutline fontSize="small" sx={{ color: "#16a34a", mr: 0.5 }} />
                            ) : null}
                            <Tooltip title="Browse products">
                              <span>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  aria-label="Browse products for Product ID"
                                  onClick={() => openPicker("productId")}
                                  disabled={!tenantId}
                                  sx={{ color: "#f97316", "&:hover": { backgroundColor: "rgba(255,132,76,0.10)" } }}
                                >
                                  <ManageSearchOutlined fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  {productIdCount === 2 && (
                    <TextField
                      label="Product ID 2"
                      placeholder="Enter product ID"
                      value={productId2}
                      onChange={(e) => handleProductIdChange("productId2", e.target.value)}
                      onPaste={(e) => handleProductIdPaste(e, "productId2")}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") findProductById("productId2", productId2);
                      }}
                      fullWidth
                      sx={FIELD_SX}
                      error={Boolean(productLookups.productId2?.error)}
                      helperText={
                        productLookups.productId2?.error ||
                        productLookups.productId2?.product?.title ||
                        (productLookups.productId2?.loading ? "Searching for product…" : " ")
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {productLookups.productId2?.loading ? (
                              <CircularProgress size={18} sx={{ color: "#f97316", mr: 0.5 }} />
                            ) : productLookups.productId2?.product ? (
                              <CheckCircleOutline fontSize="small" sx={{ color: "#16a34a", mr: 0.5 }} />
                            ) : null}
                            <Tooltip title="Browse products">
                              <span>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  aria-label="Browse products for Product ID 2"
                                  onClick={() => openPicker("productId2")}
                                  disabled={!tenantId}
                                  sx={{ color: "#f97316", "&:hover": { backgroundColor: "rgba(255,132,76,0.10)" } }}
                                >
                                  <ManageSearchOutlined fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  <Button
                    variant="contained"
                    onClick={handleSendRequest}
                    disabled={sending || !intentName || (isProductSearch && !searchText.trim()) || (productIdCount >= 1 && !productId) || (productIdCount === 2 && !productId2)}
                    startIcon={sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <Send />}
                    sx={{
                      minHeight: 56,
                      px: 3,
                      borderRadius: "16px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: "#fff",
                      whiteSpace: "nowrap",
                      background: "linear-gradient(180deg, #ff8a3d 0%, #ff6d1b 100%)",
                      boxShadow: "0 14px 30px rgba(255, 109, 27, 0.28)",
                      "&:hover": { background: "linear-gradient(180deg, #ff7a24 0%, #f2610f 100%)" },
                      "&.Mui-disabled": { background: "rgba(255, 138, 61, 0.35)", color: "#fff", boxShadow: "none" },
                    }}
                  >
                    {sending ? "Sending…" : "Send request"}
                  </Button>
                </Box>

                {intentName && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 1.25,
                      ml: 0.5,
                      color: "#9ca3af",
                      fontFamily: "'Roboto Mono', ui-monospace, monospace",
                      fontSize: "0.75rem",
                      wordBreak: "break-all",
                    }}
                  >
                    POST {INTENT_ENDPOINTS[intentName] || "/operation"}
                  </Typography>
                )}

                <TextField
                  label="Request body (JSON)"
                  placeholder={DEFAULT_BODY_PLACEHOLDER}
                  value={jsonBody}
                  onChange={(e) => {
                    const v = e.target.value;
                    setJsonBody(v);
                    if (jsonError) setJsonError("");
                  }}
                  multiline
                  minRows={10}
                  fullWidth
                  error={Boolean(jsonError)}
                  helperText={jsonError || " "}
                  sx={{
                    mt: 2.5,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "16px",
                      backgroundColor: "#fff",
                      fontFamily: "'Roboto Mono', ui-monospace, monospace",
                      fontSize: "0.85rem",
                      "& fieldset": { borderColor: "rgba(15, 23, 42, 0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(249, 115, 22, 0.35)" },
                      "&.Mui-focused fieldset": { borderColor: "#fb923c", borderWidth: "1px" },
                    },
                    "& label.Mui-focused": { color: "#f97316" },
                  }}
                />

                {responseInfo && (
                  <OperationResponsePanel result={responseInfo} onTraceIdClick={handleOpenTrace} />
                )}
              </Box>
            ) : tabValue === 1 ? (
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1.5fr) auto" },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  <TextField
                    label="Product ID"
                    placeholder="Enter product ID"
                    value={existingProductId}
                    onChange={(e) => {
                      const v = e.target.value;
                      setExistingProductId(v);
                      setExistingUrl(buildExistingProductUrl(v));
                      if (existingResult) setExistingResult(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCheckExistingProduct();
                    }}
                    fullWidth
                    sx={FIELD_SX}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Search products">
                            <span>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => openPicker("existingProductId")}
                                disabled={!tenantId}
                                sx={{ color: "#f97316", "&:hover": { backgroundColor: "rgba(255,132,76,0.10)" } }}
                              >
                                <ManageSearchOutlined fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    label="Request URL"
                    placeholder="/products/your-product-handle"
                    value={existingUrl}
                    onChange={(e) => {
                      setExistingUrl(e.target.value);
                      if (existingResult) setExistingResult(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCheckExistingProduct();
                    }}
                    fullWidth
                    sx={FIELD_SX}
                  />

                  <Button
                    variant="contained"
                    onClick={handleCheckExistingProduct}
                    disabled={existingChecking || !existingUrl.trim()}
                    startIcon={existingChecking ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <Send />}
                    sx={{
                      minHeight: 56,
                      px: 3,
                      borderRadius: "16px",
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.95rem",
                      color: "#fff",
                      whiteSpace: "nowrap",
                      background: "linear-gradient(180deg, #ff8a3d 0%, #ff6d1b 100%)",
                      boxShadow: "0 14px 30px rgba(255, 109, 27, 0.28)",
                      "&:hover": { background: "linear-gradient(180deg, #ff7a24 0%, #f2610f 100%)" },
                      "&.Mui-disabled": { background: "rgba(255, 138, 61, 0.35)", color: "#fff", boxShadow: "none" },
                    }}
                  >
                    {existingChecking ? "Checking…" : "Check product"}
                  </Button>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1.25,
                    ml: 0.5,
                    color: "#9ca3af",
                    fontFamily: "'Roboto Mono', ui-monospace, monospace",
                    fontSize: "0.75rem",
                    wordBreak: "break-all",
                  }}
                >
                  POST /products/exists · {JSON.stringify({ url: existingUrl.trim() || "/products/…" })}
                </Typography>

                {existingResult && (
                  <Box
                    sx={{
                      mt: 2.5,
                      borderRadius: "16px",
                      border: `1px solid ${existingResult.ok ? "rgba(22, 163, 74, 0.30)" : "rgba(220, 38, 38, 0.30)"}`,
                      backgroundColor: existingResult.ok ? "rgba(240, 253, 244, 0.8)" : "rgba(254, 242, 242, 0.8)",
                      p: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.85rem",
                          color: existingResult.ok ? "#15803d" : "#b91c1c",
                        }}
                      >
                        {existingResult.ok ? "Success" : "Request failed"}
                        {existingResult.status ? ` · ${existingResult.status}` : ""}
                      </Typography>
                      {existingResult.traceId && (
                        <TraceIdLink traceId={existingResult.traceId} onClick={handleOpenTrace} />
                      )}
                    </Box>
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        maxHeight: 320,
                        overflow: "auto",
                        fontFamily: "'Roboto Mono', ui-monospace, monospace",
                        fontSize: "0.8rem",
                        color: "#334155",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {existingResult.raw}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Card
                elevation={0}
                sx={{
                  maxWidth: 820,
                  borderRadius: "24px",
                  border: "1px solid rgba(255, 132, 76, 0.12)",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  boxShadow: "0 18px 40px rgba(236, 123, 55, 0.07)",
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, "&:last-child": { pb: { xs: 2.5, md: 3.5 } } }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#f97316",
                        backgroundColor: "rgba(255, 132, 76, 0.1)",
                      }}
                    >
                      <KeyOutlined />
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: "#202531", fontWeight: 800 }}>
                        Public endpoint API key
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        Retrieve, provision, or rotate the credential used to access this tenant&apos;s public endpoints.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 3, mb: credential || credentialError ? 2.5 : 0 }}>
                    <Button
                      variant="contained"
                      onClick={() => handleCredentialRequest("post")}
                      disabled={Boolean(credentialLoading) || Boolean(credential)}
                      startIcon={credentialLoading === "post" ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <KeyOutlined />}
                      sx={{
                        minHeight: 48,
                        px: 2.5,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: "#fff",
                        background: "linear-gradient(180deg, #ff8a3d 0%, #ff6d1b 100%)",
                        boxShadow: "0 12px 26px rgba(255, 109, 27, 0.24)",
                        "&:hover": { background: "linear-gradient(180deg, #ff7a24 0%, #f2610f 100%)" },
                      }}
                    >
                      {credentialLoading === "post" ? "Provisioning…" : "Provision key"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleCredentialRequest("put")}
                      disabled={Boolean(credentialLoading)}
                      startIcon={credentialLoading === "put" ? <CircularProgress size={18} sx={{ color: "#f97316" }} /> : <RefreshOutlined />}
                      sx={{
                        minHeight: 48,
                        px: 2.5,
                        borderRadius: "14px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: "#f97316",
                        borderColor: "rgba(249, 115, 22, 0.35)",
                        "&:hover": { borderColor: "#f97316", backgroundColor: "rgba(255, 247, 241, 0.72)" },
                      }}
                    >
                      {credentialLoading === "put" ? "Rotating…" : "Rotate key"}
                    </Button>
                  </Box>

                  {credential && (
                    <TextField
                      label="API key"
                      value={credential}
                      type={credentialVisible ? "text" : "password"}
                      fullWidth
                      inputProps={{ readOnly: true }}
                      helperText={credentialCopied ? "Copied to clipboard" : "Store this key securely. Rotating it immediately invalidates the previous key."}
                      sx={FIELD_SX}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={credentialVisible ? "Hide API key" : "Show API key"}>
                              <IconButton
                                aria-label={credentialVisible ? "Hide API key" : "Show API key"}
                                onClick={() => setCredentialVisible((visible) => !visible)}
                                edge="end"
                              >
                                {credentialVisible ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={credentialCopied ? "Copied" : "Copy API key"}>
                              <IconButton aria-label="Copy API key" onClick={handleCopyCredential} edge="end">
                                {credentialCopied ? <CheckCircleOutline sx={{ color: "#16a34a" }} /> : <ContentCopyOutlined />}
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  {credentialError && (
                    <Box
                      role="alert"
                      sx={{
                        mt: 1,
                        p: 1.5,
                        borderRadius: "14px",
                        color: "#b91c1c",
                        backgroundColor: "rgba(254, 242, 242, 0.9)",
                        border: "1px solid rgba(220, 38, 38, 0.2)",
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {credentialError}
                      </Typography>
                    </Box>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 2.5,
                      color: "#9ca3af",
                      fontFamily: "'Roboto Mono', ui-monospace, monospace",
                      fontSize: "0.75rem",
                      wordBreak: "break-all",
                    }}
                  >
                    GET /tenant/{tenantId}/public-endpoint-credential · POST /tenant/{tenantId}/public-endpoint-credential · PUT /tenant/{tenantId}/public-endpoint-credential
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Box>

      <ProductPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelectProduct}
        tenantId={tenantId}
      />
      <Dialog
        open={logModalOpen}
        onClose={() => setLogModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minHeight: "80vh" } }}
      >
        <DialogTitle component="div" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Agents Trace Logs
          </Typography>
          <IconButton aria-label="Close trace logs" onClick={() => setLogModalOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <LogAgents initialTraceId={selectedLogTraceId} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

ApisTab.propTypes = {
  tenants: PropTypes.array,
  selectedTenant: PropTypes.string,
  setSelectedTenant: PropTypes.func,
};

export default ApisTab;
